import styled from "styled-components/macro";
import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  IFulfilledChat,
  TMessage,
  IMessageInviteBody
} from "../../../../interfaces/interfacesChat";
import format from "date-fns/format";
import { Button } from "antd";
import { calculateHousePrice } from "../../utils";
import { useGetHouse } from "../../../../API/queries/houseApiQueries";
import { SendInviteModal } from "../../SendInviteModal";
import { parseServerDateTime } from "../../../../utils/time";
import { useHostCancelInviteWithConfirmation } from "../../../StudentInviteBox/hooks";

const Root = styled.div`
  margin-bottom: 10px;
  max-width: 500px;
`;

const Inner = styled.div`
  padding: 16px;
  background: #f4f4f4;
  border-radius: 8px;

  @media ${(props) => props.theme.media.phone} {
    font-size: 14px;
  }
`;
const TextTitle = styled.div`
  font-weight: 600;
  font-size: 18px;
  margin-bottom: 16px;

  @media ${(props) => props.theme.media.phone} {
    font-size: 14px;
  }
`;

const Text = styled.div``;
const PriceText = styled.div`
  margin-top: 16px;
`;
const Bold = styled.span`
  font-weight: 600;
`;
const Footer = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 16px;

  & > button {
    flex-grow: 1;
  }
`;

interface IHostPendingInviteMessage {
  message: TMessage;
  chat: IFulfilledChat;
}

const formatDate = (date: Date) => {
  return format(date, "d MMMM yyyy");
};

export const HostPendingInviteMessage: React.FC<IHostPendingInviteMessage> =
  React.memo(({ message, chat }) => {
    const name = chat?.conversation_partner_short?.first_name;
    const start = parseServerDateTime(
      (message.body as IMessageInviteBody).invite_dates.offered_check_in_date
    );
    const end = parseServerDateTime(
      (message.body as IMessageInviteBody).invite_dates.offered_check_out_date
    );
    const { data: house } = useGetHouse(chat.house_id);
    const [showEditInvite, setShowEditInvite] = useState(false);
    const cancelInvite = useHostCancelInviteWithConfirmation();
    const { t } = useTranslation("chats");

    const handleEdit = () => {
      setShowEditInvite(true);
    };

    return (
      <Root>
        <Inner>
          <TextTitle>
            {t(
              "systemMessages.hostPendingInviteMessage.waitingForConfirmation",
              { name: name }
            )}
          </TextTitle>
          <Text>
            {t("systemMessages.hostPendingInviteMessage.invitedMessage", {
              name: name
            })}
            <br />
            <Bold>
              {formatDate(start)} - {formatDate(end)}
            </Bold>
          </Text>
          <PriceText>
            <Trans
              i18nKey="chats:systemMessages.hostPendingInviteMessage.totalRentalPrice"
              components={[<Bold />]}
              values={{ price: house ? calculateHousePrice(house) : "" }}
            />
          </PriceText>
          <Footer>
            <Button
              onClick={() => {
                cancelInvite({
                  host_id: chat.host_id,
                  student_id: chat.student_id,
                  house_id: chat.house_id
                });
              }}
            >
              {t("systemMessages.hostPendingInviteMessage.cancel")}
            </Button>
            <Button
              type="primary"
              onClick={handleEdit}
              disabled={chat.removed_by_host}
            >
              {t("systemMessages.hostPendingInviteMessage.edit")}
            </Button>
          </Footer>
        </Inner>
        {showEditInvite ? (
          <SendInviteModal
            isShow={true}
            student={chat.conversation_partner_short!}
            hostId={chat.host_id}
            houseId={chat.house_id}
            onClose={() => {
              setShowEditInvite(false);
            }}
            onSent={() => {
              setShowEditInvite(false);
            }}
          />
        ) : null}
      </Root>
    );
  });
